/**
 * PERMISSION LAB — CLAUDECODE101 chapter 2, where three tiers meet six real modes.
 *
 * FOUNDER, 2026-08-15: "add a module in end for mastering claudecode zero to hero" ·
 * "ensure course is world class and not paper and texty".
 *
 * ── THE WHOLE DIAL AT ONCE, THEN ONE CELL AT A TIME ───────────────────────────────────────────
 * Six modes down, three actions across, and every cell filled: held, proceeds, or depends. A
 * learner sees the shape of the dial in one glance — where the safe end is, where the quiet end is,
 * and the one row that answers "depends" to everything. Then they pick a cell and read what it
 * means. That is the anti-texty form: the table is the argument, and the prose explains a cell the
 * learner chose rather than all eighteen they did not.
 *
 * ── THE ROW THAT SAYS "I DO NOT KNOW" IS THE MOST IMPORTANT ROW ───────────────────────────────
 * `auto` is described by the tool as deciding for itself when to ask. So this lab does not state
 * what it does — it says depends, and says where to find out. An earlier version filled that row
 * with confident "proceeds", including on deleting an unversioned directory, which would have been
 * a fabricated behavioural claim inside a course about not fabricating them.
 *
 * ── AND ONE POSITION IS NAMED DANGEROUS RATHER THAN HIDDEN ────────────────────────────────────
 * bypassPermissions is on the dial because it is on the tool. Omitting it would teach a menu that
 * does not exist; softening it would teach that the dial has no sharp end.
 */

import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { cited, computed, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import {
  MODES, ACTIONS, CONCEPT_TIERS, underMode, modesMatchTool,
  MAPPING_IS_A_READING, WHERE_TO_LOOK, SOURCE_TOOL, SOURCE_CONCEPT,
} from './permdial.mjs';
import { VERSION, CAPTURED_ON } from './ccfacts.mjs';

export default function PermLab() {
  const [cell, setCell] = useState({ mode: 'acceptEdits', action: 'edit' });
  const action = ACTIONS.find((a) => a.id === cell.action);
  const r = underMode(cell.mode, action);
  const tierOf = (id) => CONCEPT_TIERS.find((t) => t.id === id);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Permission lab · the dial</div>
      <h2 className="g-h2 lab-h">You learned three. The tool has six.</h2>
      <p className="g-body">
        CodeAI101 chapter 3 taught three approval tiers, and the idea was right. The tool you will
        actually run offers six named modes — and the first real decision a new user makes is
        choosing between them, usually before anyone has explained what they are.
      </p>

      {/* ── THE WHOLE DIAL ── */}
      <div className="lab-task">
        <div className="g-label">Six modes · three actions · every cell</div>
        <div className="pmd-grid" style={{ gridTemplateColumns: `minmax(150px,1.1fr) repeat(${ACTIONS.length}, 1fr)` }}>
          <div className="pmd-corner" />
          {ACTIONS.map((a) => (
            <div key={a.id} className="pmd-colh">
              <b>{a.label}</b>
              <span className="pmd-detail">{a.detail}</span>
              {!a.reversible && <span className="pmd-irrev">cannot be undone</span>}
            </div>
          ))}
          {MODES.map((m) => (
            <Row key={m.id} m={m} cell={cell} setCell={setCell} tierOf={tierOf} />
          ))}
        </div>
        <p className="g-body g-dim pmd-legend">
          <b>held</b> — nothing happens until you say so · <b>proceeds</b> — it happens ·
          <b> depends</b> — the tool decides, and the help text does not say how
        </p>
      </div>

      {/* ── THE CHOSEN CELL ── */}
      <div className="lab-task">
        <div className="g-label">
          <code>--permission-mode {r.mode.id}</code> · {action.label}
        </div>
        <div className={`pmd-out ${r.held === true ? 'pmd-held' : r.held === false ? 'pmd-go' : 'pmd-dep'}`}>
          {r.held === null
            ? <Value of={absent()} />
            : <Value of={computed(r.outcome)} />}
          {r.held === null && <span className="pmd-outtext"> {r.outcome}</span>}
        </div>
        <p className="g-body pmd-plain"><b>{r.mode.plain}</b></p>
        <p className="g-body">{r.mode.feels}</p>
        {r.note && <p className="g-body pmd-note">{r.note}</p>}
        {r.irreversibleWarning && (
          <p className="g-body pmd-warn">
            This action <b>cannot be undone</b>, and under this mode you may not be asked first.
            CodeAI101 chapter 3: not a wrong sentence you could re-read and catch — a wrong action.
          </p>
        )}
        {r.mode.dangerous && (
          <p className="g-body pmd-danger">
            <b>This is the sharp end of the dial.</b> It is shown because it is on the tool. A course
            that left it out would be teaching a menu that does not exist.
          </p>
        )}
        <p className="g-body g-dim">
          Which of CodeAI101's three tiers this is: <b>{tierOf(r.mode.tier).name}</b> — {tierOf(r.mode.tier).gist}.
          <br />{MAPPING_IS_A_READING}
        </p>
      </div>

      {/* ── PROVENANCE ── */}
      <div className="lab-task">
        <div className="g-label">Where these six came from</div>
        <p className="g-body">
          <Value of={cited(`the six mode names, read from \`claude --help\``, SOURCE_TOOL)} />
        </p>
        <p className="g-body">
          <Value of={cited('the three approval tiers this maps onto', SOURCE_CONCEPT)} />
        </p>
        <div className="lab-readout">
          <div><span className="g-label">Captured against</span><Value of={computed(VERSION)} /></div>
          <div><span className="g-label">On</span><Value of={computed(CAPTURED_ON)} /></div>
          <div><span className="g-label">Still matches the tool</span>
            <Value of={computed(modesMatchTool() ? 'yes' : 'NO — re-run the gate')} /></div>
        </div>
        <p className="g-body g-dim">{WHERE_TO_LOOK}</p>
      </div>

      <p className="g-body g-dim lab-why">
        Nothing here runs <code>claude</code>. Every mode name was read off a real machine and is
        re-checked by a gate that runs <code>claude --help</code> again and fails if the tool has
        moved — because a course about a tool, written from an assistant's memory of the tool, is
        the wrapper failure chapter 1 of CodeAI101 teaches you to spot.
        <br />
        The <b>auto</b> row says “depends” on purpose. The tool describes that mode as deciding for
        itself when to ask, so what it does with any particular action is not something the help
        text states. An earlier version of this lab filled that row in confidently, including for
        deleting an unversioned directory. It was wrong to, and saying so is cheaper than a learner
        finding out the hard way.
      </p>
    </div>
  );
}

function Row({ m, cell, setCell, tierOf }) {
  return (
    <>
      <button className={`pmd-rowh ${cell.mode === m.id ? 'pmd-sel' : ''} ${m.dangerous ? 'pmd-rowdanger' : ''}`}
        onClick={() => setCell((c) => ({ ...c, mode: m.id }))}>
        <code>{m.id}</code>
        <span className="pmd-tier">{tierOf(m.tier).name}</span>
      </button>
      {ACTIONS.map((a) => {
        const r = underMode(m.id, a);
        const on = cell.mode === m.id && cell.action === a.id;
        const klass = r.held === true ? 'pmd-cheld' : r.held === false ? 'pmd-cgo' : 'pmd-cdep';
        return (
          <button key={a.id} className={`pmd-cell ${klass} ${on ? 'pmd-on' : ''}`}
            onClick={() => setCell({ mode: m.id, action: a.id })}>
            {r.held === true ? 'held' : r.held === false ? 'proceeds' : 'depends'}
            {r.irreversibleWarning && <span className="pmd-bang">!</span>}
          </button>
        );
      })}
    </>
  );
}
